import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FiPhone, FiMail, FiMapPin, FiClock, FiPlus, FiMinus, FiFacebook, FiInstagram, FiYoutube, FiMessageCircle } from 'react-icons/fi';
import { BiCar, BiBus } from 'react-icons/bi';
import './Contact.css';

const Contact = () => {
    // FAQ State
    const [openFaq, setOpenFaq] = useState(null);

    // Fade-up variants
    const fadeUp = {
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
    };

    const staggerContainer = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.2 }
        }
    };

    const toggleFaq = (index) => {
        if (openFaq === index) {
            setOpenFaq(null);
        } else {
            setOpenFaq(index);
        }
    };

    const faqs = [
        { question: "What is the capacity of Prasannalakshmi Kalyana Mandapam & Auditorium?", answer: "Up to 1200 guests in the Grand Hall" },
        { question: "Do you provide catering services?", answer: "Yes, in-house multi-cuisine catering" },
        { question: "Is the venue air-conditioned?", answer: "Yes, fully air-conditioned" },
        { question: "Do you have parking facilities?", answer: "Yes, 500+ vehicle capacity with valet" },
        { question: "Can we bring external decorators?", answer: "Yes, or use our in-house decoration team" },
        { question: "What are the booking charges?", answer: "Contact us for customized packages" },
        { question: "Do you have rooms for the bride and groom?", answer: "Yes, private suites available" },
        { question: "Is the venue accessible for differently-abled guests?", answer: "Yes, wheelchair ramps and elevator" },
        { question: "Do you provide sound and lighting?", answer: "Yes, professional AV equipment included" },
        { question: "What is the cancellation policy?", answer: "Flexible cancellation with advance notice" }
    ];

    // Form state
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
    });

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        // Handle form submission logic
        console.log("Form submitted", formData);
        alert("Thank you for your message. We will get back to you soon!");
        setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    };

    const [heroRef, heroInView] = useInView({ triggerOnce: true, threshold: 0.1 });
    const [cardsRef, cardsInView] = useInView({ triggerOnce: true, threshold: 0.1 });
    const [locRef, locInView] = useInView({ triggerOnce: true, threshold: 0.1 });
    const [parkingRef, parkingInView] = useInView({ triggerOnce: true, threshold: 0.1 });
    const [formRef, formInView] = useInView({ triggerOnce: true, threshold: 0.1 });
    const [socialRef, socialInView] = useInView({ triggerOnce: true, threshold: 0.1 });
    const [faqRef, faqInView] = useInView({ triggerOnce: true, threshold: 0.1 });
    const [footerRef, footerInView] = useInView({ triggerOnce: true, threshold: 0.1 });

    return (
        <div className="contact-page">
            {/* 1. Page Hero */}
            <section className="contact-hero" ref={heroRef}>
                <div className="contact-hero-bg"></div>
                <div className="contact-hero-overlay"></div>
                <motion.div 
                    className="contact-hero-content"
                    variants={fadeUp}
                    initial="hidden"
                    animate={heroInView ? "visible" : "hidden"}
                >
                    <h1>Get In Touch</h1>
                    <p>We Would Love to Hear From You</p>
                </motion.div>
            </section>

            {/* 2. Quick Contact Cards */}
            <section className="contact-cards-section" ref={cardsRef}>
                <motion.div 
                    className="contact-cards-container"
                    variants={staggerContainer}
                    initial="hidden"
                    animate={cardsInView ? "visible" : "hidden"}
                >
                    <motion.div className="contact-card" variants={fadeUp}>
                        <div className="contact-card-icon"><FiPhone /></div>
                        <h3>Phone</h3>
                        <p>+91 94470 25460</p>
                    </motion.div>
                    <motion.div className="contact-card" variants={fadeUp}>
                        <div className="contact-card-icon"><FiMail /></div>
                        <h3>Email</h3>
                        <p>info@prasannalakshmi.com</p>
                    </motion.div>
                    <motion.div className="contact-card" variants={fadeUp}>
                        <div className="contact-card-icon"><FiMapPin /></div>
                        <h3>Address</h3>
                        <p>Prasannalakshmi Kalyana Mandapam & Auditorium, Mercy College Road, Pallipuram Pg, Palakkad-678006, Kerala</p>
                    </motion.div>
                    <motion.div className="contact-card" variants={fadeUp}>
                        <div className="contact-card-icon"><FiClock /></div>
                        <h3>Hours</h3>
                        <p>Mon-Sun 9:00 AM - 9:00 PM</p>
                    </motion.div>
                </motion.div>
            </section>

            {/* 3. Elegant Location Presentation */}
            <section className="location-section" ref={locRef}>
                <motion.div 
                    className="location-container"
                    variants={staggerContainer}
                    initial="hidden"
                    animate={locInView ? "visible" : "hidden"}
                >
                    <motion.div className="location-info" variants={fadeUp}>
                        <h2>Find Us</h2>
                        <div className="location-details">
                            <p className="loc-desc">Conveniently situated to make your journey as smooth as your event.</p>
                            <ul>
                                <li><strong>From Palakkad Junction:</strong> 15 min drive</li>
                                <li><strong>From Coimbatore:</strong> 1 hour drive</li>
                                <li><strong>Nearest Airport:</strong> Coimbatore International</li>
                                <li><strong>Nearest Railway:</strong> Palakkad Junction</li>
                            </ul>
                        </div>
                    </motion.div>
                    <motion.div className="location-map" variants={fadeUp}>
                        <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d673.8376068736968!2d76.63829596898137!3d10.769648904690483!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ba87207b1f9a863%3A0xa76d0fd87fc23019!2sPrasannalakshmi%20Kalyana%20Mandapam%20%26%20Auditorium!5e1!3m2!1sen!2sin!4v1788333005555!5m2!1sen!2sin" width="600" height="450" style={{ border: 0 }} allowfullscreen="" loading="lazy" referrerpolicy="strict-origin-when-cross-origin"></iframe>
                    </motion.div>
                </motion.div>
            </section>

            {/* 4. Parking Information */}
            <section className="parking-section" ref={parkingRef}>
                <motion.div 
                    className="parking-container"
                    variants={staggerContainer}
                    initial="hidden"
                    animate={parkingInView ? "visible" : "hidden"}
                >
                    <motion.div className="parking-icon-wrap" variants={fadeUp}>
                        <BiCar />
                    </motion.div>
                    <motion.h2 variants={fadeUp}>Spacious Parking for 500+ Vehicles</motion.h2>
                    <motion.p variants={fadeUp}>
                        We understand the importance of hassle-free arrival. Our extensive parking facilities include dedicated valet services, shaded and covered parking zones, and a separate spacious area for buses and coaches. Your guests' convenience starts the moment they arrive.
                    </motion.p>
                    <motion.div className="parking-features" variants={fadeUp}>
                        <div className="pf-item"><BiCar /> <span>Valet Parking</span></div>
                        <div className="pf-item"><BiCar /> <span>Covered Parking</span></div>
                        <div className="pf-item"><BiBus /> <span>Bus/Coach Area</span></div>
                    </motion.div>
                </motion.div>
            </section>

            {/* 5. Contact Form */}
            <section className="contact-form-section" ref={formRef}>
                <motion.div 
                    className="contact-form-container"
                    variants={staggerContainer}
                    initial="hidden"
                    animate={formInView ? "visible" : "hidden"}
                >
                    <motion.div className="form-header" variants={fadeUp}>
                        <h2>Send a Message</h2>
                        <p>Have a question or looking to book an event? Let us know.</p>
                    </motion.div>
                    <motion.form className="contact-form" onSubmit={handleSubmit} variants={fadeUp}>
                        <div className="form-row">
                            <div className="form-group">
                                <label>Your Name</label>
                                <input type="text" name="name" value={formData.name} onChange={handleInputChange} required placeholder="John Doe" />
                            </div>
                            <div className="form-group">
                                <label>Your Email</label>
                                <input type="email" name="email" value={formData.email} onChange={handleInputChange} required placeholder="john@example.com" />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <label>Phone Number</label>
                                <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} required placeholder="+91 94470 25460" />
                            </div>
                            <div className="form-group">
                                <label>Subject</label>
                                <select name="subject" value={formData.subject} onChange={handleInputChange} required>
                                    <option value="" disabled>Select a subject</option>
                                    <option value="General Enquiry">General Enquiry</option>
                                    <option value="Event Booking">Event Booking</option>
                                    <option value="Pricing">Pricing</option>
                                    <option value="Feedback">Feedback</option>
                                    <option value="Other">Other</option>
                                </select>
                            </div>
                        </div>
                        <div className="form-group full-width">
                            <label>Your Message</label>
                            <textarea name="message" value={formData.message} onChange={handleInputChange} required placeholder="How can we help you?" rows="5"></textarea>
                        </div>
                        <button type="submit" className="submit-btn">Send Message</button>
                    </motion.form>
                </motion.div>
            </section>

            {/* 6. Social Media Section */}
            <section className="social-section" ref={socialRef}>
                <motion.div 
                    className="social-container"
                    variants={staggerContainer}
                    initial="hidden"
                    animate={socialInView ? "visible" : "hidden"}
                >
                    <motion.h2 variants={fadeUp}>Connect With Us</motion.h2>
                    <div className="social-cards">
                        <motion.a href="#" className="social-card facebook" variants={fadeUp}>
                            <FiFacebook />
                            <span>Facebook</span>
                        </motion.a>
                        <motion.a href="#" className="social-card instagram" variants={fadeUp}>
                            <FiInstagram />
                            <span>Instagram</span>
                        </motion.a>
                        <motion.a href="#" className="social-card youtube" variants={fadeUp}>
                            <FiYoutube />
                            <span>YouTube</span>
                        </motion.a>
                        <motion.a href="#" className="social-card whatsapp" variants={fadeUp}>
                            <FiMessageCircle />
                            <span>WhatsApp</span>
                        </motion.a>
                    </div>
                </motion.div>
            </section>

            {/* 7. FAQ Section */}
            <section className="faq-section" ref={faqRef}>
                <motion.div 
                    className="faq-container"
                    variants={staggerContainer}
                    initial="hidden"
                    animate={faqInView ? "visible" : "hidden"}
                >
                    <motion.h2 variants={fadeUp} className="faq-title">Frequently Asked Questions</motion.h2>
                    <div className="faq-list">
                        {faqs.map((faq, index) => (
                            <motion.div 
                                className={`faq-item ${openFaq === index ? 'active' : ''}`} 
                                key={index}
                                variants={fadeUp}
                            >
                                <div className="faq-question" onClick={() => toggleFaq(index)}>
                                    <h3>{faq.question}</h3>
                                    <span className="faq-icon">
                                        {openFaq === index ? <FiMinus /> : <FiPlus />}
                                    </span>
                                </div>
                                <AnimatePresence>
                                    {openFaq === index && (
                                        <motion.div 
                                            className="faq-answer"
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: "auto", opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            transition={{ duration: 0.3 }}
                                        >
                                            <div className="faq-answer-content">{faq.answer}</div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </motion.div>
                        ))}
                    </div>
                </motion.div>
            </section>

            {/* 8. Final Booking Invitation Footer Section */}
            <section className="contact-cta-section" ref={footerRef}>
                <motion.div 
                    className="contact-cta-container"
                    variants={fadeUp}
                    initial="hidden"
                    animate={footerInView ? "visible" : "hidden"}
                >
                    <h2>Ready to Begin Your Celebration Journey?</h2>
                    <div className="cta-buttons">
                        <button className="cta-btn primary">Book Now</button>
                        <button className="cta-btn secondary">Call Us</button>
                    </div>
                </motion.div>
            </section>
        </div>
    );
};

export default Contact;
